import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { normalizeRole, UserRole } from '@/utils/roles';

const toSlug = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.toLowerCase();
};

export interface AuthUser extends User {
  role?: UserRole;
  schoolId?: string;
  schoolSlug?: string;
  schoolUuid?: string;
  schoolCode?: string;
  isFirstLogin?: boolean;
  hasCompletedOnboarding?: boolean;
  accessibleSchools?: {
    id: string;
    slug: string;
    uuid?: string | null;
    domain: string | null;
    name: string | null;
    weaviateId?: string | null;
  }[];
}

interface AuthContextType {
  user: AuthUser | null;
  session: Session | null;
  loading: boolean;
  markOnboardingComplete: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function enrichUserWithRole(user: User): Promise<AuthUser> {
  try {
    const appMetadata = (user.app_metadata ?? {}) as Record<string, unknown>;
    const userMetadata = (user.user_metadata ?? {}) as Record<string, unknown>;

    // Fetch role and profile in parallel for better performance
    const [roleResult, profileResult] = await Promise.all([
      supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .maybeSingle(),
      supabase
        .from('profiles')
        .select('school_id, school_slug')
        .eq('id', user.id)
        .maybeSingle()
    ]);

    if (roleResult.error) {
      console.error('Error fetching user role:', roleResult.error);
    }

    if (profileResult.error) {
      console.error('Error fetching user profile:', profileResult.error);
    }

    const profileSchoolId = profileResult.data?.school_id as string | undefined;
    const profileSchoolSlug = toSlug(
      (profileResult.data as { school_slug?: string | null } | null)?.school_slug ?? profileSchoolId
    );

    const accessibleSchoolSlugs = new Set<string>();
    const legacySchoolIds = new Set<string>();
    const slugToUuid = new Map<string, string>();

    if (profileSchoolSlug) {
      accessibleSchoolSlugs.add(profileSchoolSlug);
    } else if (profileSchoolId) {
      legacySchoolIds.add(profileSchoolId);
    }

    try {
      const { data: enrollmentCourses, error: enrollmentError } = await supabase
        .from('enrollments')
        .select('course_id')
        .eq('user_id', user.id);

      if (enrollmentError) {
        console.error('Error fetching enrollment courses:', enrollmentError);
      } else {
        const courseIds = (enrollmentCourses ?? [])
          .map((row: any) => row.course_id)
          .filter((value): value is string => Boolean(value));

        if (courseIds.length > 0) {
          try {
            const { data: courseDetails, error: courseError } = await supabase
              .from('courses')
              .select('id, school_id')
              .in('id', courseIds);

            if (courseError) {
              console.error('Error fetching course schools:', courseError);
            } else {
              (courseDetails ?? []).forEach((course: any) => {
                if (course?.school_id) {
                  legacySchoolIds.add(course.school_id);
                }
              });
            }
          } catch (courseException) {
            console.error('Unexpected error resolving course schools:', courseException);
          }
        }
      }
    } catch (enrollmentException) {
      console.error('Unexpected error fetching enrollment courses:', enrollmentException);
    }

    let accessibleSchools: {
      id: string;
      slug: string;
      uuid?: string | null;
      domain: string | null;
      name: string | null;
      weaviateId?: string | null;
    }[] = [];

    const schoolDetails: any[] = [];

    if (accessibleSchoolSlugs.size > 0) {
      const { data: slugMatches, error: slugError } = await supabase
        .from('schools')
        .select('id, slug, domain, name, settings')
        .in('slug', Array.from(accessibleSchoolSlugs));

      if (slugError) {
        console.error('Error fetching schools by slug:', slugError);
      } else if (slugMatches) {
        schoolDetails.push(...slugMatches);
      }
    }

    if (legacySchoolIds.size > 0) {
      const { data: legacyMatches, error: legacyError } = await supabase
        .from('schools')
        .select('id, slug, domain, name, settings')
        .in('id', Array.from(legacySchoolIds));

      if (legacyError) {
        console.error('Error fetching schools by legacy id:', legacyError);
      } else if (legacyMatches) {
        schoolDetails.push(...legacyMatches);
      }
    }

    if (schoolDetails.length > 0) {
      const mapped = schoolDetails
        .map((school) => {
          const settings = (school as any)?.settings ?? {};
          const slug = toSlug((school as any)?.slug ?? (settings?.weaviate_school_id as string | undefined) ?? school.id);
          const weaviateId = typeof settings?.weaviate_school_id === 'string'
            ? settings.weaviate_school_id
            : null;

          if (!slug) {
            return null;
          }

          const uuid = typeof school?.id === 'string' ? school.id : null;

          if (uuid) {
            slugToUuid.set(slug, uuid);
          }

          return {
            id: slug,
            slug,
            uuid,
            domain: school.domain ?? null,
            name: school.name ?? null,
            weaviateId,
          };
        })
        .filter((school): school is { id: string; domain: string | null; name: string | null; weaviateId?: string | null } => Boolean(school));

      const deduped: typeof mapped = [];
      const seenSlugs = new Set<string>();
      mapped.forEach((school) => {
        if (!seenSlugs.has(school.id)) {
          seenSlugs.add(school.id);
          deduped.push(school);
        }
      });

      accessibleSchools = deduped;

      accessibleSchools.forEach((school) => {
        accessibleSchoolSlugs.add(school.id);
      });
    }

    const metadataSchoolSlug = toSlug(userMetadata.school_id as string | undefined);
    const primarySchoolSlug = profileSchoolSlug
      ?? metadataSchoolSlug
      ?? accessibleSchools[0]?.id
      ?? (profileSchoolId ? toSlug(profileSchoolId) : undefined);
    const primarySchoolUuid = (profileSchoolId && profileSchoolId.trim() ? profileSchoolId : undefined)
      ?? (primarySchoolSlug ? slugToUuid.get(primarySchoolSlug) ?? undefined : undefined)
      ?? (metadataSchoolSlug ? slugToUuid.get(metadataSchoolSlug) ?? undefined : undefined)
      ?? accessibleSchools[0]?.uuid
      ?? undefined;

    let schoolCode = userMetadata.school_code as string | undefined;
    if (!schoolCode && accessibleSchools.length > 0) {
      const preferred = accessibleSchools.find(s => s.id === profileSchoolSlug) ?? accessibleSchools[0];
      schoolCode = preferred?.id ?? preferred?.weaviateId ?? preferred?.domain ?? schoolCode;
    }

    if (!schoolCode && typeof userMetadata.school_id === 'string') {
      schoolCode = toSlug(userMetadata.school_id) ?? userMetadata.school_id;
    }

    const resolvedSchoolCode = schoolCode ?? primarySchoolSlug ?? accessibleSchools[0]?.id ?? 'default';

    const dbRole = normalizeRole(roleResult.data?.role as string | undefined);
    const metadataRole = normalizeRole(
      (appMetadata.app_role as string | undefined) ??
      (userMetadata.app_role as string | undefined) ??
      (userMetadata.role as string | undefined),
    );
    const fallbackRole =
      dbRole ??
      metadataRole ??
      (user.email?.toLowerCase() === 'test@cbd.com' ? 'student' : undefined);

    return {
      ...user,
      role: fallbackRole,
      schoolId: primarySchoolSlug ?? primarySchoolUuid,
      schoolSlug: primarySchoolSlug,
      schoolUuid: primarySchoolUuid,
      schoolCode: resolvedSchoolCode,
      isFirstLogin: profileResult.data?.is_first_login ?? false,
      hasCompletedOnboarding: profileResult.data?.has_completed_onboarding ?? false,
      accessibleSchools,
    };
  } catch (error) {
    console.error('Unexpected error enriching user:', error);
    const fallbackRole =
      user.email?.toLowerCase() === 'test@cbd.com' ? 'student' : undefined;
    return {
      ...user,
      role: fallbackRole,
      schoolId: undefined,
      schoolCode: undefined,
      isFirstLogin: false,
      hasCompletedOnboarding: false,
      accessibleSchools: [],
    };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const markOnboardingComplete = async () => {
    if (!user?.id) {
      console.error('No user logged in');
      return;
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          is_first_login: false,
          has_completed_onboarding: true,
        })
        .eq('id', user.id);

      if (error) {
        console.error('Error updating onboarding status:', error);
        throw error;
      }

      // Update local user state
      setUser((prevUser) => {
        if (!prevUser) return null;
        return {
          ...prevUser,
          isFirstLogin: false,
          hasCompletedOnboarding: true,
        };
      });
    } catch (error) {
      console.error('Failed to mark onboarding complete:', error);
      throw error;
    }
  };

  useEffect(() => {
    let isMounted = true;
    let initialSessionResolved = false;

    const handleSession = async (nextSession: Session | null, source: 'initial' | 'subscription') => {
      if (!isMounted) {
        return;
      }

      setSession(nextSession);

      if (!nextSession?.user) {
        if (source === 'subscription' && !initialSessionResolved) {
          // 初期セッション確定前のnullイベントは無視する
          return;
        }
        setUser(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const enrichedUser = await enrichUserWithRole(nextSession.user);
        if (isMounted) {
          setUser(enrichedUser);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Failed to enrich user after sign-in:', error);
          setUser({ ...(nextSession.user as AuthUser) });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        if (!initialSessionResolved && !newSession) {
          return;
        }
        void handleSession(newSession, 'subscription');
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      initialSessionResolved = true;
      void handleSession(session, 'initial');
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, loading, markOnboardingComplete }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export type { UserRole } from '@/utils/roles';
